import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';
import {cacheResult, HttpService} from 'projects/core/src/public-api';
import {ProjectActivity} from './project-activity';
import * as moment from 'moment';
import {ActivityType} from './activity-type';
import {map, tap} from 'rxjs/operators';
import {environment} from 'projects/desktop/src/environments/environment';
import {EntityEventEmitter} from 'projects/core/src/lib/core/data/entities/entity-event-emitters';
import {Memoize} from 'projects/core/src/lib/core/utils/decorators/memoize-decorator';
import {ActivityOverlap} from './activity-overlap';
import {FileService} from 'projects/core/src/lib/misc/data/file/file.service';
import {HttpEvent} from '@angular/common/http';
import {ProjectFile} from '../project-file';
import {Any, JsonConvert} from 'json2typescript';
import {ActivityQuote} from './activity-quote';
import {OrderEmail} from 'projects/desktop/src/app/modules/order/data/order-email';
import {ActivityInitData} from './activity-init-data';


@Injectable({
  providedIn: 'root',
})
export class ProjectActivityService {
  projectActivityEmitter = new EntityEventEmitter<ProjectActivity>();

  constructor(
    protected http: HttpService,
    protected fileService: FileService
  ) {
  }

  getActivityProjects(dateFrom: Date, dateTo: Date, typeIds?: number[], userIds?: number[]): Observable<any> {
    const startDate = moment(dateFrom).format('YYYY-MM-DD');
    const endDate = moment(dateTo).format('YYYY-MM-DD');

    let url = `/project/activity/schedule-list/${startDate}:${endDate}`;
    const params = [];

    if (typeIds) {
      params.push(`typeIds=${typeIds.join(',')}`);
    }

    if (userIds) {
      params.push(`userIds=${userIds.join(',')}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http.getData(url, ProjectActivity)
  }

  getDeliveryProjects(dateFrom: Date, dateTo: Date): Observable<any> {
    const startDate = moment(dateFrom).format('YYYY-MM-DD');
    const endDate = moment(dateTo).format('YYYY-MM-DD');

    const url = `/project/activity/delivery-list/${startDate}:${endDate}`;
    return this.http.getData(url, ProjectActivity)
  }

  @Memoize()
  getActivityTypes(): Observable<ActivityType[]> {
    return this.http
      .getData('/activity/types/enabled', ActivityType)
      .pipe(
        cacheResult(environment.dataDefaultCacheTime)
      );
  }

  @Memoize()
  getMajorActivityTypes(): Observable<ActivityType[]> {
    return this.http
      .getData('/activity/types/major', ActivityType)
      .pipe(
        cacheResult(environment.dataDefaultCacheTime)
      )
  }

  saveProjectActivity(projectActivity: ProjectActivity, preventToPast?: boolean): Observable<ProjectActivity> {
    const activityUrl = preventToPast === undefined ? '/project/activity/update' : `/project/activity/update?preventMovingToPast=${preventToPast}`

    return this.http
      .putData(activityUrl, projectActivity, ProjectActivity, projectActivity instanceof ProjectActivity)
      .pipe(
        tap(updatedActivity => {
          this.projectActivityEmitter.emitUpdate(updatedActivity);
        }),
        map(responseData => {
          return this.http.fromJson(responseData, ProjectActivity);
        })
      )
  }

  getProjectActivities(projectId: number): Observable<ProjectActivity[]> {
    return this.http.getData(`/project/activity/list/project/${projectId}`, ProjectActivity);
  }

  getHolidays(startDate: string, endDate: string): Observable<any[]> {
    return this.http.doHttpGet(`/holiday/list/${startDate}:${endDate}`);
  }

  checkOverlapping(projectId: number, activity: ProjectActivity): Observable<ActivityOverlap> {
    return this.http.postData(`/project/activity/check-overlaps/for-project/${projectId}`, activity, ActivityOverlap)
  }

  createProjectActivity(projectId: number, typeName: string, projectActivityData: ProjectActivity): Observable<ProjectActivity> {
    return this.http.postData(`/project/activity/create/for-project/${projectId}/type/${typeName}`, projectActivityData, ProjectActivity, true)
      .pipe(
        tap(createdActivity => {
          this.projectActivityEmitter.emitAdd(createdActivity);
        }))
  }

  uploadFile(files: File[], activityId: number): Observable<HttpEvent<ProjectFile[]>> {
    return this.fileService.postFile<ProjectFile[]>(this.fileService.getUploadFileLink('activity', activityId), 'files', files);
  }

  deleteFile(fileId: number, activityId: number): Observable<ProjectFile> {
    return this.http.deleteData(this.fileService.getDeleteFileLink(fileId), ProjectFile)
  }

  deleteProjectActivities(projectActivityId: number): Observable<void> {
    return this.http.deleteData(`/project/activity/delete/${projectActivityId}`, ProjectActivity).pipe(
      tap(() => {
        this.projectActivityEmitter.emitRemove(projectActivityId);
      })
    )
  }

  getActivityDays(typeId: number, startDate: string, endDate: string): Observable<any> {
    return this.http.doHttpGet(`/project/activity/schedule?typeId=${typeId}&dateFrom=${startDate}&dateTo=${endDate}`)
  }

  getServiceOrdersDays(typeId: number, startDate: string, endDate: string): Observable<any> {
    return this.http.doHttpGet(`/project/activity/schedule?typeId=${typeId}&dateFrom=${startDate}&dateTo=${endDate}&serviceOrder=true`)
  }

  getActivityQuotes(activityId: number): Observable<ActivityQuote[]> {
    return this.http.getData(`/notification/installation-quote/list/for-activity/${activityId}`, ActivityQuote)
  }

  updateActivityQuote(quote: ActivityQuote): Observable<ActivityQuote> {
    return this.http.putData(`/notification/installation-quote`, quote, ActivityQuote)
  }

  getActivityQuoteEmail(quoteId: number): Observable<OrderEmail> {
    return this.http.getData(`/notification/installation-quote/${quoteId}/email-body`, OrderEmail);
  }

  getActivityAwardEmail(quoteId: number): Observable<OrderEmail> {
    return this.http.getData(`/notification/installation-quote/${quoteId}/award/email-body`, OrderEmail);
  }

  sendActivityAwardEmail(quoteId: number, emails: string, message: string): Observable<OrderEmail> {
    return this.http.putData(`/notification/installation-quote/${quoteId}/award`, {emails, message}, OrderEmail);
  }

  sendActivityQuoteEmail(quoteId: number, emails: string, message: string): Observable<OrderEmail> {
    return this.http.putData(`/notification/${quoteId}/send-to-client`, {emails, message}, OrderEmail)
  }

  cancelActivityQuoteEmail(quoteId: number): Observable<OrderEmail> {
    return this.http.putData(`/notification/installation-quote/${quoteId}/cancel`, {}, OrderEmail)
  }

  recalculateActivities(projectId: number): Observable<ProjectActivity[]> {
    return this.http.doHttpPut(`/project/${projectId}/activities/recalculate`, null).pipe(
        map((response: any) => {
          return response as ProjectActivity[];
        })
    ).pipe(
        tap(activities => {
          activities.forEach(activity => {
            this.projectActivityEmitter.emitUpdate(activity);
          })
        })
    )
  }

  deleteActivities(projectId: number): Observable<number[]> {
    return this.http.doHttpDelete(`/project/${projectId}/activities`).pipe(
        map((response: any) => {
          return response as number[];
        })
    ).pipe(
      tap(ids => {
        ids.forEach(id => {
          this.projectActivityEmitter.emitRemove(id);
        })
      })
    )
  }

  getActivityInitData(projectId: number, activityType: string): Observable<ActivityInitData> {
    return this.http.getData(`/project/activity/init-data/for-project/${projectId}/type/${activityType}`, ActivityInitData);
  }

  calculateEndDate(typeName: string, dateFrom: string, durationSeconds: number): Observable<string> {
    return this.http.doHttpGet(`/project/activity/get-end-date/type/${typeName}?dateFrom=${dateFrom}&duration=${durationSeconds}`);
  }

  calculateDuration(typeName: string, dateFrom: string, dateTo: string): Observable<number> {
    return this.http.doHttpGet(`/project/activity/get-duration/type/${typeName}?dateFrom=${dateFrom}&dateTo=${dateTo}`);
  }

  splitActivity(activityId: number, splitDate: string): Observable<any> {
    return this.http.doHttpPut(`/project/activity/split/${activityId}/${splitDate}`, null);
  }

}
